import {ChevronLeft} from 'lucide-react'
import React from 'react'
import styled, {css} from 'styled-components'

const StyledButton = styled.button<{$showLabel: boolean}>`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  /* The labelled form reads as a link: link ink, underline on hover. */
  ${({$showLabel}) =>
    $showLabel &&
    css`
      color: var(--text-link);

      &:hover {
        color: var(--text-link-hover);
        text-decoration: underline;
      }
    `}
`

interface BackNavigationButtonProps {
  /** The destination page. It is the accessible name, and the visible text when `showLabel` is set. */
  label: string
  onClick: () => void
  /**
   * Renders a small chevron plus the visible label, in link colour. Off by default: the chevron
   * alone, named by `aria-label`.
   */
  showLabel?: boolean
  /** Allows theming (e.g. light text on a dark header) via styled() or a parent rule. */
  className?: string
  'data-testid'?: string
}

export const BackNavigationButton: React.FC<BackNavigationButtonProps> = ({
  label,
  onClick,
  showLabel = false,
  className,
  'data-testid': testId = 'back-navigation-button',
}) => (
  <StyledButton
    type="button"
    onClick={onClick}
    className={className}
    data-testid={testId}
    $showLabel={showLabel}
    // The visible text names the labelled form; an aria-label as well would only repeat it.
    aria-label={showLabel ? undefined : label}>
    <ChevronLeft size={showLabel ? 16 : 24} aria-hidden />
    {showLabel && label}
  </StyledButton>
)
